// src/hooks/useSessionSync.js
//
// Keeps the browser's step data and the server in sync:
//  - autosave (debounced ~800 ms, on blur and on step change) with optimistic versions,
//  - a localStorage mirror of unsynced drafts (restored on reload, pushed when back online),
//  - save status for the UI: "saved" | "saving" | "offline" | "error" | "idle".
//
// The server stays the source of truth. Every localStorage access is wrapped in try/catch.

import { useCallback, useEffect, useRef, useState } from "react";
import { saveStep } from "../api";

export const EDITABLE_STEPS = [1, 2, 3, 4, 6, 7];
const DEBOUNCE_MS = 800;
const MIRROR_PREFIX = "t4r_ljd_drafts_v1:";

export const canonical = (value) => JSON.stringify(value ?? null);

function readMirror(key) {
  try {
    const raw = window.localStorage.getItem(MIRROR_PREFIX + key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMirror(key, value) {
  try {
    if (!value || Object.keys(value).length === 0) {
      window.localStorage.removeItem(MIRROR_PREFIX + key);
    } else {
      window.localStorage.setItem(MIRROR_PREFIX + key, JSON.stringify(value));
    }
  } catch {
    /* storage may be unavailable (private mode, quota) -- the server still has the data */
  }
}

export function clearAllMirrors() {
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(MIRROR_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/**
 * @param {object} opts
 * @param {string|null} opts.mirrorKey   per-session key for the localStorage mirror
 * @param {object} opts.localData        {step: data} built from the UI state
 * @param {(step:number, view:object)=>void} opts.onSaved   called with the server's step view
 * @param {(step:number, current:object)=>void} opts.onConflict  server has a newer version
 * @param {(step:number, err:Error)=>void} opts.onError
 */
export function useSessionSync({ mirrorKey, localData, onSaved, onConflict, onError }) {
  const [saveState, setSaveState] = useState("idle");
  const versions = useRef({}); // step -> server version
  const synced = useRef({}); // step -> canonical data last known on the server
  const inFlight = useRef(false);
  const timer = useRef(null);
  const retryTimer = useRef(null);
  const retryDelay = useRef(2000);
  const latest = useRef(localData);
  const callbacks = useRef({ onSaved, onConflict, onError });
  latest.current = localData;
  callbacks.current = { onSaved, onConflict, onError };

  /** Record what the server has for a step (after hydration, confirm, LLM run, ...). */
  const markSynced = useCallback((step, data, version) => {
    synced.current[step] = canonical(data);
    if (typeof version === "number") versions.current[step] = version;
  }, []);

  const dirtySteps = useCallback(() => {
    if (!mirrorKey) return [];
    return EDITABLE_STEPS.filter(
      (n) => latest.current[n] !== undefined && synced.current[n] !== undefined &&
        canonical(latest.current[n]) !== synced.current[n]
    );
  }, [mirrorKey]);

  const flush = useCallback(async () => {
    if (!mirrorKey || inFlight.current) return;
    const steps = dirtySteps();
    if (steps.length === 0) {
      setSaveState((s) => (s === "saving" || s === "offline" ? "saved" : s));
      return;
    }
    inFlight.current = true;
    setSaveState("saving");
    let offline = false;
    try {
      for (const n of steps) {
        const data = latest.current[n];
        try {
          const view = await saveStep(n, data, versions.current[n] ?? 0);
          versions.current[n] = view.version;
          synced.current[n] = canonical(data);
          callbacks.current.onSaved?.(n, view);
        } catch (err) {
          if (err.network) {
            offline = true;
            break;
          }
          if (err.code === "version_conflict" && err.current) {
            versions.current[n] = err.current.version;
            synced.current[n] = canonical(err.current.data);
            callbacks.current.onConflict?.(n, err.current);
          } else {
            // Validation errors etc.: do not retry the same payload in a loop.
            synced.current[n] = canonical(data);
            callbacks.current.onError?.(n, err);
          }
        }
      }
    } finally {
      inFlight.current = false;
    }
    if (offline) {
      setSaveState("offline");
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => flush(), retryDelay.current);
      retryDelay.current = Math.min(retryDelay.current * 2, 30000);
    } else {
      retryDelay.current = 2000;
      // Data may have changed while saving.
      if (dirtySteps().length > 0) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => flush(), DEBOUNCE_MS);
      } else {
        setSaveState("saved");
      }
    }
  }, [mirrorKey, dirtySteps]);

  // Debounced autosave whenever local data changes.
  const signature = canonical(localData);
  useEffect(() => {
    if (!mirrorKey) return undefined;
    const dirty = dirtySteps();
    // Mirror unsynced drafts locally so a reload or a lost connection does not lose work.
    const mirror = {};
    dirty.forEach((n) => {
      mirror[n] = { data: latest.current[n], version: versions.current[n] ?? 0 };
    });
    writeMirror(mirrorKey, mirror);
    if (dirty.length === 0) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [signature, mirrorKey, dirtySteps, flush]);

  // Save on blur (any field losing focus) and when the connection comes back.
  useEffect(() => {
    if (!mirrorKey) return undefined;
    const onBlur = () => flush();
    const onOnline = () => flush();
    document.addEventListener("focusout", onBlur, true);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("focusout", onBlur, true);
      window.removeEventListener("online", onOnline);
      clearTimeout(retryTimer.current);
    };
  }, [mirrorKey, flush]);

  /** Drafts saved locally but not on the server (e.g. made offline before a reload). */
  const readUnsyncedMirror = useCallback(() => (mirrorKey ? readMirror(mirrorKey) : {}), [mirrorKey]);

  /** Local data differs from what the server last accepted for this step. */
  const isDirty = useCallback(
    (step) => synced.current[step] !== undefined && canonical(latest.current[step]) !== synced.current[step],
    []
  );

  /** Resolves once no autosave request is in flight (Confirm waits for it, so a late autosave
   *  reply cannot overwrite the confirmed state on screen). */
  const idle = useCallback(async () => {
    clearTimeout(timer.current);
    for (let i = 0; inFlight.current && i < 300; i += 1) {
      await new Promise((r) => setTimeout(r, 50));
    }
  }, []);

  return { saveState, flush, markSynced, versions, readUnsyncedMirror, dirtySteps, isDirty, idle };
}
